import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router';
import posthog from 'posthog-js';
import {
  fetchUserDetails,
  fetchLocations,
  fetchAllSensorsInLocation,
  fetchLocationProperties,
  fetchAvailableVars,
} from '../services/apiService';
import {
  setUserPublic,
  setLocationData,
  setSensorsById,
  setAvailableVars,
  setCurrentLocation,
} from '../state/actions';
import useStyles from '../styles';

import MobileViewDrawer from '../Widgets/Navigation/MobileViewDrawer';
import DesktopViewDrawer from '../Widgets/Navigation/DesktopViewDrawer';
import MainWrapper from './MainWrapper';
import { SensorLatest } from '../services/api';
import { retrieveState, StorageTypes } from '../utils/persistentState';
import { useAppDispatch } from '../state/store';
import { routes } from './helpers';

interface ShellProps {
  userName: string;
}

function Shell({ userName }: ShellProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { classes } = useStyles();

  useEffect(() => {
    if (userName) {
      fetchUserDetails()
        .then((userDetails) => {
          dispatch(setUserPublic(userDetails));
        })
        .catch(() => {
          dispatch(setUserPublic(null));
        });

      fetchLocationProperties('#').then((location) => {
        // Add a property for the user to help identify their location in posthog
        posthog.capture('Root Location', { rootLocName: location?.name });
      });

      fetchLocations()
        .then((locations) => {
          dispatch(setLocationData(locations));
          // check if stored location is valid
          const storedLocation: string =
            (retrieveState(StorageTypes.CurrentLocation) as string) ?? '#';
          const isValidLoc = locations?.locations?.find(
            (locDetail) => locDetail.id === storedLocation
          );
          if (!isValidLoc) {
            dispatch(setCurrentLocation('#'));
          }
        })
        .catch(() => {
          dispatch(setLocationData(null));
        });

      fetchAllSensorsInLocation('#', ['name', 'location'])
        .then((sensors) => {
          const sensorProps = sensors.sensors ?? [];
          dispatch(setSensorsById(sensorProps as SensorLatest[]));
        })
        .catch(() => null);

      fetchAvailableVars()
        .then((vars) => {
          dispatch(setAvailableVars(vars));
        })
        // keep all vars for error
        .catch(() => dispatch(setAvailableVars([])));
    }
  }, [userName, dispatch]);

  return (
    <Router>
      <>
        <MobileViewDrawer />
        <DesktopViewDrawer />
        <div
          className={`${classes.shell} no-print`}
          style={{ minHeight: window.innerHeight ? window.innerHeight : '100vh' }}
        >
          <Routes>
            {routes.map((route) => (
              <Route
                path={route.url}
                key={route.id}
                element={
                  <MainWrapper
                    routeComponent={<route.component />}
                    helmetTitle={route.helmetTitle}
                  />
                }
              />
            ))}
          </Routes>
        </div>
      </>
    </Router>
  );
}

export default Shell;
